<template>
  <div class="user-management-page">
    <b-card no-body class="p-1">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-1 top-toolbar">
        <v-select
          v-model="perPage"
          :clearable="false"
          :searchable="false"
          class="entries-select"
          :options="['08', '10', '20', '50']"
          @input="reloadPageOne"
        />

        <div class="d-flex align-items-center flex-wrap right-controls">
          <b-form-input
            v-model="search"
            placeholder="Search name, phone, email"
            class="search-input"
          />
          <v-select
            v-model="statusFilter"
            :clearable="false"
            :searchable="false"
            class="filter-select"
            :reduce="option => option.value"
            label="label"
            :options="statusOptions"
          />
          <AdminDatePicker
            v-model="joinedFrom"
            placeholder="Joined from"
            class="date-input"
          />
          <AdminDatePicker
            v-model="joinedTo"
            placeholder="Joined to"
            class="date-input"
            :min-date="joinedFrom || null"
          />
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-2">
        <b-spinner label="Loading users..." />
      </div>
      <b-alert v-else-if="loadError" show variant="danger" class="mb-1">
        {{ loadError }}
        <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchUsers">Retry</b-button>
      </b-alert>
      <div v-else-if="!rows.length" class="text-center text-muted py-2">
        No records found.
      </div>
      <b-table v-else :items="rows" :fields="fields" responsive class="mb-0 users-table" tbody-tr-class="user-row">
        <template #cell(no)="data">
          <span>{{ String(data.item.no).padStart(2, '0') }}</span>
        </template>

        <template #cell(name)="data">
          <b-link
            :to="{ name: 'client-profile', params: { id: String(data.item.id) } }"
            class="name-link d-flex align-items-center"
          >
            <b-avatar size="36" :src="data.item.profileImage" variant="light-primary" class="mr-1 name-avatar" />
            <span>{{ data.item.name }}</span>
          </b-link>
        </template>

        <template #cell(following)="data">
          <b-avatar-group size="28">
            <b-avatar v-for="(avatar, i) in data.item.following.slice(0, 3)" :key="i" :src="avatar" />
            <b-avatar
              v-if="data.item.extra > 0"
              variant="light-secondary"
              :text="`+${data.item.extra}`"
            />
          </b-avatar-group>
          <span class="ml-50 following-count">{{ data.item.followingCount }}</span>
        </template>

        <template #cell(actions)="data">
          <row-actions
            show-view
            @view="openUserProfile(data.item)"
            @edit="openUserEdit(data.item)"
            @delete="confirmRemoveUser(data.item)"
          />
        </template>
      </b-table>

      <AdminPaginationBar
        :page="currentPage"
        :per-page="perPageNum"
        :total="totalRows"
        @change="onPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BAvatarGroup,
  BButton,
  BCard,
  BFormInput,
  BLink,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import AdminDatePicker from '@/components/admin/AdminDatePicker.vue'
import RowActions from '@/components/admin/RowActions.vue'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  applyListPagination,
  debounce,
  formatAdminDate,
  parseAdminDateForApi,
  resolveMediaUrl,
  unwrapAdminResponse,
} from '@/utils/adminApiHelpers'
import { confirmAction, notifyError, notifySuccess } from '@/utils/uiActions'

export default {
  components: {
    BAlert,
    BAvatar,
    BAvatarGroup,
    BButton,
    BCard,
    BFormInput,
    BLink,
    BSpinner,
    BTable,
    vSelect,
    AdminDatePicker,
    RowActions,
    AdminPaginationBar,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      currentPage: 1,
      perPage: '08',
      totalRows: 0,
      search: '',
      statusFilter: '',
      joinedFrom: '',
      joinedTo: '',
      statusOptions: [
        { label: 'All statuses', value: '' },
        { label: 'Active', value: 'active' },
        { label: 'Suspended', value: 'suspended' },
        { label: 'Blocked', value: 'blocked' },
      ],
      fields: [
        { key: 'no', label: 'NO' },
        { key: 'name', label: 'NAME' },
        { key: 'following', label: 'FOLLOWING' },
        { key: 'status', label: 'STATUS' },
        { key: 'joined', label: 'JOINED' },
        { key: 'contact', label: 'CONTACT' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      rows: [],
    }
  },
  computed: {
    perPageNum() {
      return parseInt(this.perPage, 10) || 8
    },
  },
  watch: {
    search() {
      this.debouncedSearch()
    },
    statusFilter() {
      this.reloadPageOne()
    },
    joinedFrom() {
      this.debouncedSearch()
    },
    joinedTo() {
      this.debouncedSearch()
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.currentPage = 1
      this.fetchUsers()
    }, 400)
    this.fetchUsers()
  },
  methods: {
    reloadPageOne() {
      this.currentPage = 1
      this.fetchUsers()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchUsers()
    },
    formatCount(n) {
      const num = Number(n) || 0
      if (num >= 1000) return `${Math.round(num / 100) / 10}K`
      return String(num)
    },
    mapUserRow(user) {
      const preview = (user.following?.preview || []).map(p => resolveMediaUrl(p.profile_image))
      const count = Number(user.following?.count || 0)
      return {
        id: user.id,
        no: user.no,
        name: user.name,
        following: preview,
        extra: Math.max(0, count - 3),
        followingCount: this.formatCount(count),
        status: user.status,
        joined: formatAdminDate(user.joined_date),
        contact: user.contact,
        profileImage: resolveMediaUrl(user.profile_image),
      }
    },
    async fetchUsers() {
      this.isLoading = true
      this.loadError = ''
      try {
        const term = this.search.trim()
        const params = {
          page: this.currentPage,
          per_page: this.perPageNum,
          account_type: 'app_user',
        }
        if (term) params.search = term
        if (this.statusFilter) params.status = this.statusFilter
        const joinedFrom = parseAdminDateForApi(this.joinedFrom)
        const joinedTo = parseAdminDateForApi(this.joinedTo)
        if (joinedFrom) params.joined_from = joinedFrom
        if (joinedTo) params.joined_to = joinedTo

        const res = await adminPanelApi.users(params)
        const payload = unwrapAdminResponse(res)
        applyListPagination(this, res, payload)
        this.rows = (payload.users || []).map(u => this.mapUserRow(u))
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load users.'
        this.rows = []
        this.totalRows = 0
      } finally {
        this.isLoading = false
      }
    },
    openUserProfile(item) {
      this.$router.push({ name: 'client-profile', params: { id: String(item.id) } })
    },
    openUserEdit(item) {
      this.$router.push({ name: 'client-edit', params: { id: String(item.id) } })
    },
    confirmRemoveUser(item) {
      confirmAction(
        this,
        {
          title: 'Remove user?',
          text: `Remove ${item.name}? This cannot be undone.`,
          confirmButtonText: 'Remove',
        },
        async () => {
          try {
            await adminPanelApi.deleteUser(item.id)
            notifySuccess(this, 'User removed successfully.')
            await this.fetchUsers()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to remove user.')
          }
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.user-management-page {
  padding-top: 0.35rem;
}

.top-toolbar {
  row-gap: 0.75rem;
}

.right-controls {
  column-gap: 0.65rem;
}

.entries-select {
  width: 82px;
  min-width: 82px;
}

.search-input {
  width: 210px;
}

.filter-select {
  width: 150px;
}

.date-input {
  width: 150px;
}

.name-avatar {
  font-weight: 600;
}

.name-link {
  color: #4b465c;
  font-weight: 500;
  text-decoration: none;
}

.name-link:hover {
  color: #0b1f39;
  text-decoration: none;
}

.following-count {
  font-size: 0.85rem;
  font-weight: 500;
}

::v-deep .users-table th {
  font-size: 0.76rem;
  letter-spacing: 0.04rem;
  color: #6e6b7b;
}

::v-deep .users-table td,
::v-deep .users-table th {
  padding-top: 0.9rem;
  padding-bottom: 0.9rem;
  vertical-align: middle;
}

::v-deep .page-item.active .page-link {
  background-color: #0b1f39 !important;
  border-color: #0b1f39 !important;
}

@media (max-width: 767px) {
  .right-controls {
    width: 100%;
  }

  .search-input,
  .filter-select,
  .date-input {
    width: 100%;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
